import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '../UI/LoadingButton';
import LanguageSelect from '../App/Generator/LanguageSelect';
import FAQ from '../UI/FAQ';
import './PublicSummaryTool.css';

const PublicSummaryTool = () => {
    const [videoUrl, setVideoUrl] = useState('');
    const [summary, setSummary] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasUsedTool, setHasUsedTool] = useState(false);
    const [language, setLanguage] = useState('english');
    const [showSignupPrompt, setShowSignupPrompt] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const hasUsed = localStorage.getItem('hasUsedSummaryTool');
        if (hasUsed) {
            setHasUsedTool(true);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (hasUsedTool) {
            setShowSignupPrompt(true);
            return;
        }

        setLoading(true);
        setError('');
        setShowSignupPrompt(false);

        try {
            const response = await api.post('/public/generate/summary', {
                url: videoUrl,
                language
            });

            if (response.data.result === 'OK') {
                setSummary(response.data.text);
                localStorage.setItem('hasUsedSummaryTool', 'true');
                setHasUsedTool(true);
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            setError('An error occurred while summarizing the video.');
        } finally {
            setLoading(false);
        }
    };

    const formatContent = (content) => {
        return content.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
        ));
    };

    const languageField = LanguageSelect(false, language, setLanguage);

    const faqQuestions = [
        {
            question: "Is this YouTube video summarizer really free?",
            answer: "Yes, you can try our AI YouTube Video Summarizer completely free. After your first summary, you'll need to create an account to continue using the service and access unlimited summaries.",
            icon: "fa-dollar-sign"
        },
        {
            question: "How accurate are the AI-generated YouTube summaries?",
            answer: "Our AI-powered YouTube summarizer uses advanced language models to ensure high accuracy. The quality depends on the video's audio clarity, content structure, and available subtitles.",
            icon: "fa-bullseye"
        },
        {
            question: "Are there any YouTube video length limitations for summarizing?",
            answer: "There are no strict limitations on video length for our YouTube summary tool, as long as YouTube subtitles or captions are available for processing.",
            icon: "fa-clock"
        },
        {
            question: "How can I use the YouTube video summaries?",
            answer: "You can use our YouTube summaries for research, content creation, study notes, quick video understanding, blog post creation, or social media content. Simply copy and paste the summary wherever you need it.",
            icon: "fa-lightbulb"
        },
        {
            question: "What languages does the YouTube summarizer support?",
            answer: "Our YouTube video summarizer supports multiple languages including English, Spanish, French, German, Italian, Portuguese, and many more for both input and output.",
            icon: "fa-globe"
        },
        {
            question: "Can I summarize private or unlisted YouTube videos?",
            answer: "Our YouTube summary tool can only process publicly available YouTube videos with accessible subtitles or captions.",
            icon: "fa-lock"
        }
    ];

    // Schema.org structured data
    const webApplicationSchema = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "AI YouTube Video Summarizer",
        "description": "Free AI-powered YouTube video summarizer tool. Generate instant, accurate summaries of any YouTube video. Save time with intelligent video content extraction and analysis.",
        "url": "https://ytcopycat.com/youtube_summary",
        "applicationCategory": "MultimediaApplication",
        "operatingSystem": "Web Browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "creator": {
            "@type": "Organization",
            "name": "YT Copycat"
        },
        "featureList": [
            "AI-powered YouTube video summarization",
            "Instant video content analysis",
            "Multi-language summary support",
            "Free YouTube summary generation",
            "No registration required for first use",
            "Accurate video transcript processing",
            "Time-saving video insights"
        ]
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqQuestions.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Summarize YouTube Videos with AI",
        "description": "Step-by-step guide to generate AI summaries of YouTube videos using our free summarizer tool",
        "step": [
            {
                "@type": "HowToStep",
                "name": "Copy YouTube Video URL",
                "text": "Go to the YouTube video you want to summarize and copy the URL from your browser's address bar."
            },
            {
                "@type": "HowToStep",
                "name": "Paste URL in Summarizer",
                "text": "Paste the YouTube video URL into our AI summarizer tool input field."
            },
            {
                "@type": "HowToStep",
                "name": "Select Summary Language",
                "text": "Choose your preferred language for the video summary output."
            },
            {
                "@type": "HowToStep",
                "name": "Generate AI Summary",
                "text": "Click 'Generate Summary' and our AI will analyze the video content and create an instant summary."
            }
        ]
    };

    return (
        <div className="public-summary-tool yt-thumbnail-page">
            <Helmet>
                <title>Free AI YouTube Video Summarizer - Instant Video Summary Generator | YT Copycat</title>
                <meta name="description" content="Generate instant AI summaries of any YouTube video for free. Best YouTube video summarizer tool with advanced AI technology. Save time with accurate video content analysis and intelligent summarization." />
                <meta name="keywords" content="youtube video summarizer, ai youtube summarizer, summarize youtube video, youtube summary generator, youtube video summary ai, ai summarize youtube video, youtube summary tool, free youtube summarizer, youtube video summarizer ai, youtube summarizer free, youtube summary ai, video summarizer online, youtube content summarizer, youtube video summary generator, youtube transcript summarizer, ai video summary, youtube video analysis, youtube summary maker, youtube video digest, smart youtube summarizer, youtube video insights, youtube summary extractor, youtube video briefing, youtube summary creator, youtube video overview generator" />
                
                {/* Open Graph tags */}
                <meta property="og:title" content="Free AI YouTube Video Summarizer - Instant Video Summary Generator" />
                <meta property="og:description" content="Generate instant AI summaries of any YouTube video for free. Advanced AI technology creates accurate video summaries in seconds. Save time with intelligent video analysis." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://ytcopycat.com/youtube_summary" />
                <meta property="og:image" content="https://ytcopycat.com/images/youtube-summarizer-og.jpg" />
                <meta property="og:site_name" content="YT Copycat" />
                
                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Free AI YouTube Video Summarizer - Instant Video Summary Generator" />
                <meta name="twitter:description" content="Generate instant AI summaries of any YouTube video for free. Advanced AI technology creates accurate video summaries in seconds." />
                <meta name="twitter:image" content="https://ytcopycat.com/images/youtube-summarizer-twitter.jpg" />
                
                {/* Additional SEO meta tags */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />
                <meta name="author" content="YT Copycat" />
                <meta name="publisher" content="YT Copycat" />
                <meta name="language" content="en" />
                <meta name="geo.region" content="US" />
                <meta name="theme-color" content="#1a1a1a" />
                
                {/* Canonical and alternate links */}
                <link rel="canonical" href="https://ytcopycat.com/youtube_summary" />
                <link rel="alternate" hrefLang="en" href="https://ytcopycat.com/youtube_summary" />
                
                {/* Schema.org structured data */}
                <script type="application/ld+json">
                    {JSON.stringify(webApplicationSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(howToSchema)}
                </script>
            </Helmet>

            <Navbar />
            
            <main className="yt-thumbnail-main">
                <div className="yt-thumbnail-container">
                    <div className="yt-thumbnail-header">
                        <h1>AI YouTube Video Summarizer</h1>
                        <p>Get straight to the key points of any YouTube video without spending hours watching. Our AI-powered YouTube summarizer generates accurate, concise video summaries in seconds.</p>
                    </div>

                    <section className="tool-features-compact" aria-label="YouTube summarizer features">
                        <article className="tool-feature-item">
                            <div className="icon">
                                <i className="fas fa-bolt" aria-hidden="true"></i>
                            </div>
                            <h3>100% Free</h3>
                            <p>Summarize your first video completely free. No registration required.</p>
                        </article>

                        <article className="tool-feature-item">
                            <div className="icon">
                                <i className="fas fa-robot" aria-hidden="true"></i>
                            </div>
                            <h3>AI-Powered</h3>
                            <p>Advanced artificial intelligence ensures precise and relevant summaries.</p>
                        </article>

                        <article className="tool-feature-item">
                            <div className="icon">
                                <i className="fas fa-clock" aria-hidden="true"></i>
                            </div>
                            <h3>Save Time</h3>
                            <p>Get key insights from long videos in seconds instead of hours.</p>
                        </article>
                    </section>

                    <section className="tool-form-enhanced">
                        <form onSubmit={handleSubmit} role="search" aria-label="YouTube video summarizer form">
                            <div className="yt-thumbnail-input-group">
                                <label htmlFor="videoUrl">YouTube Video URL</label>
                                <input
                                    type="url"
                                    id="videoUrl"
                                    className="yt-thumbnail-input"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                                    required
                                />
                            </div>

                            <div className="yt-thumbnail-input-group">
                                <label htmlFor={languageField.id}>Summary Language</label>
                                <select
                                    id={languageField.id}
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    className="yt-thumbnail-input"
                                >
                                    {languageField.options.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <LoadingButton
                                type="submit"
                                loading={loading}
                                className="button-plain"
                            >
                                {loading ? 'Generating Summary...' : 'Generate Summary'}
                            </LoadingButton>
                        </form>

                        {error && !hasUsedTool && (
                            <div className="error-message" style={{marginTop: '1rem'}} role="alert">
                                {error}
                            </div>
                        )}

                        {showSignupPrompt && (
                            <section className="signup-card" aria-label="Sign up for more YouTube summaries">
                                <i className="fas fa-lock" aria-hidden="true"></i>
                                <h3>Ready for More Summaries?</h3>
                                <p>You've discovered how powerful our AI summarizer is! Sign up now to:</p>
                                <ul>
                                    <li><i className="fas fa-check" aria-hidden="true"></i> Generate unlimited video summaries</li>
                                    <li><i className="fas fa-check" aria-hidden="true"></i> Access advanced AI models</li>
                                    <li><i className="fas fa-check" aria-hidden="true"></i> Create other content formats</li>
                                </ul>
                                <button 
                                    className="button-plain" 
                                    onClick={() => navigate('/signup')}
                                >
                                    Start Summarizing for Free
                                </button>
                            </section>
                        )}

                        {summary && (
                            <section className="yt-thumbnail-result" aria-label="Generated YouTube video summary">
                                <h2 className="yt-thumbnail-title">Video Summary</h2>
                                <div className="summary-content">
                                    {formatContent(summary)}
                                </div>
                            </section>
                        )}
                    </section>

                    <div className="tool-section-divider"></div>

                    <section className="section" aria-label="Who benefits from YouTube video summarizer">
                        <div className="section-header">
                            <h4>PERFECT FOR</h4>
                            <h2>Who Can Benefit from Our AI YouTube Video Summarizer?</h2>
                            <p>Our YouTube summary tool is designed to help various users save time and increase productivity</p>
                        </div>
                        <div className="use-cases-list">
                            <article className="use-case-item">
                                <i className="fas fa-graduation-cap" aria-hidden="true"></i>
                                <h3>Students & Researchers</h3>
                                <p>Quickly extract key information from educational YouTube videos and lectures.</p>
                            </article>
                            <article className="use-case-item">
                                <i className="fas fa-briefcase" aria-hidden="true"></i>
                                <h3>Business Professionals</h3>
                                <p>Stay informed with concise summaries of industry talks and webinars.</p>
                            </article>
                            <article className="use-case-item">
                                <i className="fas fa-pen" aria-hidden="true"></i>
                                <h3>Content Creators</h3>
                                <p>Generate quick summaries for your content research and blog posts.</p>
                            </article>
                        </div>
                    </section>

                    <div className="tool-section-divider"></div>

                    <section className="section" aria-label="How to use YouTube video summarizer">
                        <div className="section-header">
                            <h4>HOW IT WORKS</h4>
                            <h2>Get an AI YouTube Summary in 4 Easy Steps</h2>
                        </div>
                        <div className="steps-list">
                            <article className="step-item">
                                <div className="step-number">01</div>
                                <div className="step-content">
                                    <h3>Paste URL</h3>
                                    <p>Enter the YouTube video URL you want to summarize.</p>
                                </div>
                            </article>
                            <article className="step-item">
                                <div className="step-number">02</div>
                                <div className="step-content">
                                    <h3>AI Analysis</h3>
                                    <p>Our AI analyzes the video content and transcript.</p>
                                </div>
                            </article>
                            <article className="step-item">
                                <div className="step-number">03</div>
                                <div className="step-content">
                                    <h3>Generate Summary</h3>
                                    <p>Get a concise summary with key points in seconds.</p>
                                </div>
                            </article>
                            <article className="step-item">
                                <div className="step-number">04</div>
                                <div className="step-content">
                                    <h3>Save Time</h3>
                                    <p>Quickly grasp video content without watching the full video.</p>
                                </div>
                            </article>
                        </div>
                    </section>

                    <div className="tool-section-divider"></div>

                    <section className="tool-benefits-section" aria-label="Benefits of AI YouTube video summarization">
                        <h2>Why Choose Our AI YouTube Video Summarizer?</h2>
                        <div className="tool-benefits-grid">
                            <article className="tool-benefit-item">
                                <h3>Instant Processing</h3>
                                <p>Our YouTube summary generator processes videos instantly, providing immediate results.</p>
                            </article>
                            <article className="tool-benefit-item">
                                <h3>Multi-Language Support</h3>
                                <p>Generate video summaries in multiple languages for global accessibility.</p>
                            </article>
                            <article className="tool-benefit-item">
                                <h3>Accurate Technology</h3>
                                <p>Advanced algorithms ensure high-quality summaries that capture essential content.</p>
                            </article>
                            <article className="tool-benefit-item">
                                <h3>No Installation</h3>
                                <p>Use directly in your browser - no downloads or plugins required.</p>
                            </article>
                        </div>
                    </section>

                    <FAQ 
                        title="YouTube Video Summarizer - Frequently Asked Questions"
                        questions={faqQuestions}
                    />
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PublicSummaryTool; 